import { MiddlewareAPI, Dispatch, Action } from 'redux'
import { deleteRequest, urlBuilder } from 'tfs.front-end-utils/requests'

import ReduxStore from '../../interfaces/client/ReduxStore'
import ClientPassengerSSRInterface from '../../interfaces/client/PassengerSSR'

import staticData from '../../config/staticData'

import {
  addPassengers,
  passengersFetched,
  removePassengerSSR,
  receiveDeletePassengers,
} from '../../actions/passengers'
import { REQUEST_DELETE_PASSENGERS } from '../../actions/passengers/requestDeletePassengers'
import { closePassengersToBeDeletedConfirmation } from '../../actions/modals'
import addNotification from '../../actions/notifications/addNotification'

import getLabels from '../../utils/getLabels'
import generateAuthorizationHeaders from '../../utils/requests/generateAuthorizationHeaders'
import customTourOperatorParameters from '../../utils/tourOperators/customTourOperatorParameters'
import {
  buildClientPassenger,
  addPassengerSSRsToPassengers,
} from '../../utils/passengers'
import filterUniqueStrings from '../../utils/filterUniqueStrings'
import getPnrFromPassengerId from '../../utils/getPnrFromPassengerId'
import {
  dispatchSuccessNotification,
  dispatchErrorNotifications,
  buildGenericNotification,
  buildServerNotification,
} from '../../utils/notifications'

export interface RemovePassengersAction extends Action {
  passengerIds: string[]
  flightId: string
}

export default (store: MiddlewareAPI) => (next: Dispatch) => async (
  action: RemovePassengersAction
) => {
  next(action)

  if (action.type !== REQUEST_DELETE_PASSENGERS) return

  const { passengerIds, flightId } = action
  const dispatch = store.dispatch

  try {
    const {
      endpoints: {
        allotments: { url, headers },
      },
    } = staticData

    const fixedUrl = url.endsWith('/') ? url : `${url}/`
    const requestUrl = urlBuilder(
      `${fixedUrl}${flightId}/passengers`,
      customTourOperatorParameters()
    )

    const authHeaders = await generateAuthorizationHeaders(headers)

    const deleteResponse = await deleteRequest(requestUrl, {
      headers: { ...headers, ...authHeaders },
      body: JSON.stringify(passengerIds),
    })

    if (deleteResponse.errors.length) {
      dispatchErrorNotifications(dispatch, [
        ...deleteResponse.errors,
        buildGenericNotification(getLabels('error_removing_passengers')),
      ])
    } else {
      dispatch(receiveDeletePassengers(passengerIds))
      dispatchSuccessNotification(
        dispatch,
        getLabels('success_your_changes_have_been_made')
      )
    }

    if (deleteResponse.passengers.length) {
      const state: ReduxStore = store.getState()
      // 1. Delete all relevant passengers in ReduxStore with an old PNR
      const getPassengerIdsThatMatchPnr = (pnr: string) =>
        state.passengers.allIds.filter((passengerId: string) =>
          passengerId.includes(pnr)
        )

      const passengerIdsToBeRemoved = action.passengerIds
        .map(getPnrFromPassengerId)
        .reduce(filterUniqueStrings, [])
        .map((pnr: string) => getPassengerIdsThatMatchPnr(pnr))
        .flat()

      dispatch(receiveDeletePassengers(passengerIdsToBeRemoved))

      // 2. Remove SSRs for deleted passengers
      passengerIdsToBeRemoved.forEach((passengerId: string) => {
        const passengerSSRs = state.passengerSSRs.byId[passengerId]

        if (!passengerSSRs) return

        passengerSSRs.forEach((passengerSSR: ClientPassengerSSRInterface) => {
          dispatch(removePassengerSSR(passengerId, passengerSSR.passengerSSRId))
        })
      })

      // 3. Add new passengers
      const clientFormattedPassengers = deleteResponse.passengers.map(
        buildClientPassenger
      )
      dispatch(addPassengers(clientFormattedPassengers))
      dispatch(passengersFetched(clientFormattedPassengers))

      // 4. Create SSRs for newly added passengers
      addPassengerSSRsToPassengers(dispatch)(clientFormattedPassengers)
    }

    dispatch(closePassengersToBeDeletedConfirmation())
  } catch (error) {
    dispatch(addNotification(buildServerNotification(error)))
    dispatch(closePassengersToBeDeletedConfirmation())
  }
}
